import React, { useMemo } from 'react';
import { Box, Text } from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { MantineProvider } from '@mantine/core';

const UserTable = ({ 
  users = [], 
  loading = false,
  onStatusChange,
  currentOrgLeader
}) => {
  const columns = useMemo(() => [
    {
      header: 'User',
      accessorKey: 'userName',
      size: 180,
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      header: 'Email',
      accessorKey: 'email',
      size: 200,
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      header: 'Region',
      accessorKey: 'baseRegion',
      size: 100,
      enableSorting: true,
      filterVariant: 'select',
      filterSelectOptions: ['US', 'EU', 'APAC'],
      Cell: ({ cell }) => (
        <Box
          sx={(theme) => ({
            padding: '4px'
          })}
        >
          {cell.getValue() || 'N/A'}
        </Box>
      )
    },
    {
      header: 'Role',
      accessorKey: 'role',
      size: 120,
      enableSorting: true,
      filterVariant: 'select',
      filterSelectOptions: ['Admin', 'OrgAdmin', 'Leader', 'Trainee'],
    },
    {
      header: 'Status',
      accessorKey: 'subscriptionStatus',
      size: 100,
      filterVariant: 'select',
      filterSelectOptions: ['active', 'pending', 'inactive', 'cancelled'],
      Cell: ({ cell }) => (
        <Box
          sx={(theme) => ({
            borderRadius: '4px',
            color: 
              cell.getValue() === 'active' ? theme.colors.green[9] :
              cell.getValue() === 'pending' ? theme.colors.orange[9] :
              cell.getValue() === 'cancelled' ? theme.colors.red[9] :
              theme.colors.red[9],
            maxWidth: '9ch',
            padding: '4px'
          })}
        >
          {cell.getValue()}
        </Box>
      )
    },
    {
      header: 'Active',
      accessorKey: 'statusToggle',
      size: 80,
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.original.subscriptionStatus === 'active'}
          disabled={row.original.subscriptionStatus === 'pending'}
          onChange={() => {
            const newStatus = row.original.subscriptionStatus === 'active' ? 'inactive' : 'active';
            onStatusChange(row.original.email, newStatus);
          }}
        />
      )
    },
    {
      header: 'Org Lead',
      accessorKey: 'orgLead',
      size: 100,
      Cell: ({ row }) => (
        <div className="text-center">
          {row.original.email === currentOrgLeader?.email && (
            <span className="text-green-500">
              ✓
            </span>
          )}
        </div>
      )
    }
  ], [onStatusChange, currentOrgLeader]);

  const table = useMantineReactTable({
    columns,
    data: users,
    state: { isLoading: loading },
    enableColumnResizing: true,
    enableStickyHeader: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnOrdering: true,
    initialState: {
      density: 'xs',
      pagination: { pageIndex: 0, pageSize: 20 },
      sorting: [{ id: 'userName', desc: false }]
    },
    mantineTableContainerProps: { 
      sx: { 
        maxHeight: 'calc(80vh * 0.8)',
        paddingLeft: '16px',
        paddingRight: '16px',
        backgroundColor: 'white',
      } 
    },
    renderEmptyRowsFallback: () => (
      <Text style={{ display: "flex", justifyContent: "center" }}>
        No users found.
      </Text>
    ),
    mantineTopToolbarProps: {
      sx: {
        backgroundColor: 'white',
        borderRadius: '8px 8px 0 0',
        paddingTop: '0px',
        marginTop: '8px',
        marginBottom: '0px',
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        backgroundColor: '#e9ecef',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        color: '#333',
        padding: '8px 6px',
      },
    }
  });

  return (
    <MantineProvider>
      <div className="custom-table-wrapper">
        <MantineReactTable table={table} />
      </div>
    </MantineProvider>
  );
};

export default UserTable;